import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconMail, IconLock, IconBuilding } from '@tabler/icons-react';
import { login as loginService, register as registerService } from '../services/authService';
import { useAuthContext } from '../contexts/AuthContext';
import { OpsightLogo } from '../components/OpsightLogo';
import { ThemeToggle } from '../components/ThemeToggle';
import { AnimatedBackground } from '../components/AnimatedBackground';
import { useVersion } from '../hooks/useVersion';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login: loginContext } = useAuthContext();
  const { version } = useVersion();


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        if (!organizationName.trim()) {
          setError('Nome da organização é obrigatório');
          setLoading(false);
          return;
        }
        
        // Criar usuário e organização
        await registerService(email, password, organizationName.trim());
        
        // Limpar formulário e mostrar mensagem de sucesso
        setSuccess('Conta criada com sucesso! Faça login para continuar.');
        setIsRegister(false);
        setEmail('');
        setPassword('');
        setOrganizationName('');
        setLoading(false);
      } else {
        // Fazer login
        const loginResponse = await loginService(email, password);
        
        // Armazenar token e dados do usuário no contexto
        loginContext(loginResponse.token, {
          userId: loginResponse.userId,
          email: loginResponse.email,
          displayName: loginResponse.displayName,
        });
        
        // Redirecionar para o dashboard após login
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      // A mensagem de erro já vem traduzida do authService
      setError(err.message || 'Erro ao fazer login ou criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page page-center">
      <AnimatedBackground />
      <div className="container container-tight py-4" style={{ position: 'relative', zIndex: 1 }}>
        <div className="d-flex justify-content-end mb-3" style={{ position: 'relative', zIndex: 2 }}>
          <ThemeToggle />
        </div>
        <div className="text-center mb-4">
          <a href="/" className="navbar-brand navbar-brand-autodark d-inline-block mb-3">
            <OpsightLogo size={180} />
          </a>
          <p className="text-muted mt-0">Clareza para decisões de engenharia</p>
        </div>
        <div className="card card-md" style={{ position: 'relative', zIndex: 2 }}>
          <div className="card-body">
            <h2 className="card-title text-center mb-4">
              {isRegister ? 'Criar Conta' : 'Entrar'}
            </h2>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success" role="alert">
                {success}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <div className="input-group input-group-flat">
                  <span className="input-group-text">
                    <IconMail size={20} />
                  </span>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Senha</label>
                <div className="input-group input-group-flat">
                  <span className="input-group-text">
                    <IconLock size={20} />
                  </span>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              {isRegister && (
                <div className="mb-3">
                  <label className="form-label">Nome da Organização</label>
                  <div className="input-group input-group-flat">
                    <span className="input-group-text">
                      <IconBuilding size={20} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nome da sua organização"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      required
                    />
                  </div>
                  <small className="form-hint">
                    Este será o nome da sua organização
                  </small>
                </div>
              )}
              <div className="form-footer">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {isRegister ? 'Criando conta...' : 'Entrando...'}
                    </>
                  ) : (
                    isRegister ? 'Criar Conta' : 'Entrar'
                  )}
                </button>
              </div>
            </form>
          </div>
          <div className="card-footer">
            <div className="text-center text-muted">
              {isRegister ? (
                <>
                  Já tem uma conta?{' '}
                  <button
                    type="button"
                    className="btn btn-outline-primary px-3 mx-2"
                    onClick={() => {
                      setIsRegister(false);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Faça login
                  </button>
                </>
              ) : (
                <>
                  <div className="mb-3">
                    Não tem uma conta?{' '}
                    
                  </div>
                  <div>
                  <button
                      type="button"
                      className="btn btn-primary px-3 mx-3"
                      onClick={() => {
                        setIsRegister(true);
                        setError('');
                        setSuccess('');
                      }}
                    >
                      Registre-se
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/accept-invite')}
                    >
                      Tenho um convite
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {version && (
        <div
          style={{
            position: 'fixed',
            bottom: '16px',
            right: '16px',
            fontSize: '12px',
            color: 'var(--tblr-muted)',
            zIndex: 1000,
          }}
        >
          {version.shortVersion}
        </div>
      )}
    </div>
  );
};
